import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { OWNER, whatsappLink } from "@/lib/legalContent";

const NAV = [
  { label: "Inicio", to: "inicio" },
  { label: "Servicios", to: "servicios" },
  { label: "Sobre mí", to: "sobre-mi" },
  { label: "Galería", to: "galeria" },
  { label: "Opiniones", to: "opiniones" },
  { label: "Contacto", to: "contacto" },
];

const Logo = () => (
  <Link to="/" className="flex items-center gap-3" data-testid="navbar-logo">
    <span className="grid h-11 w-11 place-items-center rounded-xl bg-primary font-display text-2xl font-semibold text-primary-foreground shadow-sm">
      L
    </span>
    <span className="leading-tight">
      <span className="block font-display text-xl font-semibold text-foreground">Lorena Cuevas</span>
      <span className="block text-[10px] font-semibold uppercase tracking-[0.22em] text-primary">
        Clínica Podológica
      </span>
    </span>
  </Link>
);

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goToSection = (id) => {
    setOpen(false);
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }), 250);
    } else {
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-white/90 shadow-[0_6px_30px_-12px_rgba(0,0,0,0.15)] backdrop-blur-md" : "bg-white/70 backdrop-blur"
      }`}
    >
      <nav className="mx-auto flex max-w-7xl items-center justify-between px-5 py-3 lg:px-8">
        <Logo />

        <div className="hidden items-center gap-8 lg:flex">
          {NAV.map((n) => (
            <button
              key={n.to}
              onClick={() => goToSection(n.to)}
              data-testid={`navbar-link-${n.to}`}
              className="text-sm font-semibold text-foreground/80 transition-colors hover:text-primary"
            >
              {n.label}
            </button>
          ))}
        </div>

        <div className="hidden items-center gap-4 lg:flex">
          <a href={`tel:${OWNER.phoneBookingRaw}`} className="flex items-center gap-2 text-sm font-semibold text-foreground">
            <Phone className="h-4 w-4 text-primary" />
            {OWNER.phoneBooking}
          </a>
          <a
            href={whatsappLink()}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="navbar-cta-pedir-cita"
            className="rounded-full bg-primary px-6 py-2.5 text-sm font-bold text-primary-foreground shadow-sm transition-transform hover:-translate-y-0.5 hover:shadow-lg"
          >
            Pedir cita
          </a>
        </div>

        <button
          className="grid h-10 w-10 place-items-center rounded-lg text-foreground lg:hidden"
          onClick={() => setOpen((v) => !v)}
          data-testid="navbar-mobile-toggle"
          aria-label="Abrir menú"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </nav>

      {open && (
        <div className="border-t border-border bg-white px-5 py-4 lg:hidden" data-testid="navbar-mobile-menu">
          <div className="flex flex-col gap-1">
            {NAV.map((n) => (
              <button
                key={n.to}
                onClick={() => goToSection(n.to)}
                className="rounded-lg px-3 py-3 text-left text-base font-semibold text-foreground/85 hover:bg-secondary"
              >
                {n.label}
              </button>
            ))}
          </div>
          <a
            href={whatsappLink()}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-3 block rounded-full bg-primary px-6 py-3 text-center text-sm font-bold text-primary-foreground"
          >
            Pedir cita por WhatsApp
          </a>
        </div>
      )}
    </header>
  );
}
